import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { trackPwaInstallImpression, trackPwaInstallOutcome } from '@/lib/analytics';

const PAGE_VIEW_KEY = 'pwa-install-page-views';
const DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-install-installed';
export const SHOW_AT_PAGE_VIEW = 3;

export function readPageViewCount(): number {
  try {
    const raw = sessionStorage.getItem(PAGE_VIEW_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function shouldShowBanner(opts: {
  canInstall: boolean;
  pageViews: number;
  dismissed: boolean;
  installed: boolean;
}): boolean {
  return (
    opts.canInstall &&
    !opts.dismissed &&
    !opts.installed &&
    opts.pageViews >= SHOW_AT_PAGE_VIEW
  );
}

export function PwaInstallBanner() {
  const location = useLocation();
  const { canInstall, install } = usePwaInstall();
  const [pageViews, setPageViews] = useState<number>(() => readPageViewCount());
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(DISMISSED_KEY) === '1',
  );
  const [installed, setInstalled] = useState<boolean>(
    () => localStorage.getItem(INSTALLED_KEY) === '1',
  );

  useEffect(() => {
    const next = readPageViewCount() + 1;
    sessionStorage.setItem(PAGE_VIEW_KEY, String(next));
    setPageViews(next);
  }, [location.pathname]);

  useEffect(() => {
    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      setInstalled(true);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  const show = shouldShowBanner({ canInstall, pageViews, dismissed, installed });
  const impressionFired = useRef(false);
  useEffect(() => {
    if (show && !impressionFired.current) {
      impressionFired.current = true;
      trackPwaInstallImpression();
    }
  }, [show]);

  if (!show) {
    return null;
  }

  const handleInstall = async () => {
    trackPwaInstallOutcome('install_click');
    await install();
  };

  const handleDismiss = () => {
    trackPwaInstallOutcome('dismiss');
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Install MotoWalla app"
      className="fixed inset-x-0 bottom-0 z-[90] border-t border-border-primary bg-bg-secondary/95 px-3 py-3 shadow-2xl backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-screen-lg items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text-primary">Install MotoWalla</p>
          <p className="truncate text-xs text-text-tertiary">
            Add to home screen for faster access and offline support.
          </p>
        </div>
        <button
          onClick={handleInstall}
          className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="rounded-full p-1.5 text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
